/**
 * <hero-menu> — desktop top-nav flyout (progressive enhancement).
 *
 * The reveal itself is pure CSS (`:hover` / `:focus-within` on `.hero-menu__group`),
 * so the menu works without JS. This element only adds:
 *   - `aria-expanded` sync on the group trigger (pointer + keyboard focus),
 *   - Escape closes the currently focused/open group,
 *   - a short close-grace so quickly crossing a small gap doesn't collapse the panel.
 *
 * No Shadow DOM, no imports — mirrors the plain-custom-element style of async-section.js.
 */
class HeroMenu extends HTMLElement {
  /** @type {number | null} */
  #closeTimer = null;

  /** @constant {number} */
  #closeDelay = 150;

  connectedCallback() {
    this.addEventListener('pointerenter', this.#onEnter, true);
    this.addEventListener('pointerleave', this.#onLeave, true);
    this.addEventListener('focusin', this.#onEnter, true);
    this.addEventListener('focusout', this.#onLeave, true);
    this.addEventListener('keydown', this.#onKeydown);
  }

  disconnectedCallback() {
    this.removeEventListener('pointerenter', this.#onEnter, true);
    this.removeEventListener('pointerleave', this.#onLeave, true);
    this.removeEventListener('focusin', this.#onEnter, true);
    this.removeEventListener('focusout', this.#onLeave, true);
    this.removeEventListener('keydown', this.#onKeydown);
    if (this.#closeTimer) clearTimeout(this.#closeTimer);
  }

  /** @param {Event} event */
  #groupFor(event) {
    const target = event.target;
    if (!(target instanceof Element)) return null;
    return target.closest('[data-hero-group]');
  }

  /** @param {Event} event */
  #onEnter = (event) => {
    const group = this.#groupFor(event);
    if (!group) return;
    if (this.#closeTimer) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }
    this.#setExpanded(group, true);
  };

  /** @param {Event} event */
  #onLeave = (event) => {
    const group = this.#groupFor(event);
    if (!group) return;
    this.#closeTimer = window.setTimeout(() => this.#setExpanded(group, false), this.#closeDelay);
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    const group = this.#groupFor(event);
    if (!group) return;
    this.#setExpanded(group, false);
    const trigger = group.querySelector('.hero-menu__trigger');
    if (trigger instanceof HTMLElement) trigger.blur();
  };

  /**
   * @param {Element} group
   * @param {boolean} expanded
   */
  #setExpanded(group, expanded) {
    const trigger = group.querySelector('.hero-menu__trigger[aria-haspopup="true"]');
    if (trigger) trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  }
}

if (!customElements.get('hero-menu')) {
  customElements.define('hero-menu', HeroMenu);
}
